<template>
	<div>
		<v-container v-if="loading">
			<v-row class="contentRow" justify="center">
				<v-col cols="12" class="text-center py-12">
					<v-progress-circular indeterminate color="primary" size="48" />
					<p class="mt-4">Loading NIS survey...</p>
				</v-col>
			</v-row>
		</v-container>

		<v-container v-else-if="loadError">
			<v-row class="contentRow" justify="center">
				<v-col cols="12" md="8" class="text-center py-12">
					<v-alert type="error" prominent text>
						{{ loadError }}
					</v-alert>
					<v-btn color="primary" class="mt-4" @click="loadForm">
						Try again
					</v-btn>
					<v-btn text class="mt-4 ml-2" to="/">
						Back to portal
					</v-btn>
				</v-col>
			</v-row>
		</v-container>

		<!-- Fill survey (before submit) -->
		<form-input
			v-else-if="!viewMode && questions && questions.length > 0"
			:name="name"
			:questions="questions"
			:savedAnswers="savedAnswers"
			:inputAnswers="answers"
			:fileID="fileID"
			:receiveanswernumbers="receiveanswernumbers"
			:introText="introText"
			alertLabel="NIS"
			@submitForm="sendNisReport"
			@save="saveNisReport"
			@updateAnswerNumbers="updateAnswerNumbers"
		/>

		<!-- After submit: score + badge + invite + answers (like Sustainability Survey view) -->
		<v-container v-else-if="viewMode">
			<v-row class="contentRow">
				<v-col cols="12" class="nis-score-header">
					<h1 class="nis-score-heading">
						{{ clientName }}'s Self-Assessment Score {{ year }}
					</h1>
					<v-btn
						v-if="showFillOutButton"
						class="nis-fill-out-btn"
						style="color: white; background-color: orange;"
						elevation="0"
						@click="goToFillOut"
					>
						{{ buttonText }}
					</v-btn>
				</v-col>

				<p class="nis-score-copy">
					{{ introText }}
				</p>

				<v-col cols="12" md="7" sm="12" class="nis-dashboard-box">
					<div class="nis-box-inner">
						<nis-score-rings
							:score1="score1"
							:score2="score2"
							:score3="score3"
							:score4="score4"
							:score5="score5"
							:score6="score6"
							title="Your NIS Cyber Security Score"
						/>
						<h3 class="text-right darkColor--text nis-filled-out">
							Filled out : {{ filledOutDate }}
						</h3>
					</div>
				</v-col>

				<v-col cols="12" md="5" sm="12" class="nis-dashboard-box">
					<div class="nis-box-inner nis-badge-inner text-center">
						<h2 class="nis-badge-title">Download your NIS Cyber Security badge</h2>
						<img
							class="nis-badge-preview"
							:src="require('../assets/Badge_NIS_2026.jpg')"
							alt="NIS Cyber Security badge"
						/>
						<v-btn
							class="nis-download-btn mt-2"
							color="rgb(34, 126, 133)"
							dark
							small
							@click="downloadCanvas"
						>
							<v-icon left>fas fa-download</v-icon>
							Download
						</v-btn>
					</div>
				</v-col>

				<v-col cols="12" class="text-center">
					<v-btn
						width="60%"
						class="mx-auto"
						color="primary"
						to="/nis/request"
					>
						<v-icon left>fas fa-user-plus</v-icon>
						Invite suppliers to fill out NIS form
					</v-btn>
				</v-col>

				<canvas
					width="600"
					height="600"
					class="nis-badge-canvas"
					ref="canvas"
				/>
			</v-row>

			<br />
			<br />

			<div v-for="(question, index) in questions" :key="index">
				<div v-if="index === 0 && question.type === 'title'">
					<v-row
						class="contentRow primary"
						:style="question.color ? `background-color: ${question.color} !important; border-color: ${question.color} !important` : ''"
					>
						<v-col cols="10" class="mr-auto white--text">
							<h2>This is the Orixe NIS Cyber Security Self-Assessment for {{ year }}.</h2>
						</v-col>
					</v-row>
				</div>
				<div v-else-if="question.type === 'title'">
					<v-row
						class="contentRow primary"
						:style="question.color ? `background-color: ${question.color} !important; border-color: ${question.color} !important` : ''"
					>
						<v-col cols="6" class="mr-auto white--text">
							<h2>{{ question.name }}</h2>
						</v-col>
					</v-row>
				</div>
				<div v-else-if="question.type === 'multiplechoice'">
					<v-row class="contentRow">
						<v-col>
							<h4 class="primary--text">{{ question.name }}</h4>
							<br />
							<p v-if="!displayAnswer(index)">
								<i>This question was not answered</i>
							</p>
							<p v-else>{{ displayAnswer(index) }}</p>
							<p v-if="displayRecommendation(index)" class="mt-2">
								<strong>Recommendation:</strong> {{ displayRecommendation(index) }}
							</p>
						</v-col>
					</v-row>
				</div>
			</div>
		</v-container>
	</div>
</template>

<script>
import NisApi from "../api/nis/nis"
import LoadingState from "../consts/LoadingState"
import FormInput from "../components/FormInput.vue"
import NisScoreRings from "../components/NisScoreRings.vue"

export default {
	name: "NisSurvey",
	components: {
		"form-input": FormInput,
		"nis-score-rings": NisScoreRings,
	},
	props: {
		assessmentId: {
			type: [String, Number],
			default: null
		}
	},
	data() {
		return {
			questions: null,
			name: "NIS Cyber Security",
			id: "2026",
			fileID: null,
			savedAnswers: [],
			answers: [],
			answernumbers: [],
			receiveanswernumbers: [],
			viewMode: false,
			loading: false,
			loadError: null,
			year: new Date().getFullYear(),
			filledOutDate: "",
			score1: null,
			score2: null,
			score3: null,
			score4: null,
			score5: null,
			score6: null,
			timestamp: null,
			resubmit: 0,
			nisID: null,
			introText: "NIS stands for Network and Information Systems. The Orixe NIS Cyber Security survey is a comprehensive self-assessment tool that helps organizations measure their cybersecurity maturity in relation to the EU NIS2 Directive (the Network and Information Systems Directive). Through a questionnaire-based survey, you can evaluate your maturity across six key categories: Governance & Risk Management, Incident Handling & Reporting, Business Continuity & Crisis Management, Supply Chain & Third Parties, Technical & Operational Security, and People, Training & HR Security. This process provides an overall score for your organization while encouraging continuous improvement through honest self-reflection on your cybersecurity status."
		}
	},
	computed: {
		clientName() {
			return this.$store.getters["user/getUser"].clientName
		},
		showFillOutButton() {
			if (this.resubmit === 1) {
				return true
			}
			if (this.timestamp) {
				const timestampDate = new Date(this.timestamp)
				const newTimestampDate = new Date(Date.UTC(timestampDate.getFullYear() + 1, 0, 1, 0, 0, 0))
				const currentDate = new Date()
				if (newTimestampDate > timestampDate && currentDate > newTimestampDate) {
					return true
				}
				return false
			}
			return false
		},
		buttonText() {
			if (this.resubmit === 1) {
				return "Fill Out again"
			}
			if (this.timestamp) {
				return `Fill Out for ${new Date().getFullYear()}`
			}
			return "Fill out now"
		},
	},
	mounted() {
		this.loadForm()
	},
	methods: {
		updateAnswerNumbers(newAnswerNumbers) {
			this.answernumbers = newAnswerNumbers
		},
		parseJsonArray(raw) {
			if (raw == null || raw === "") {
				return []
			}
			if (Array.isArray(raw)) {
				return raw
			}
			if (typeof raw === "string") {
				try {
					const parsed = JSON.parse(raw)
					return Array.isArray(parsed) ? parsed : []
				} catch (e) {
					return []
				}
			}
			return []
		},
		findPossibilityIndex(question, answerText) {
			if (!question?.possibilities?.length || answerText == null || answerText === "") {
				return null
			}
			const normalized = String(answerText).trim()
			for (let p = 0; p < question.possibilities.length; p++) {
				if (normalized === String(question.possibilities[p]).trim()) {
					return p
				}
			}
			return null
		},
		resolveAnswerIndex(index) {
			const question = this.questions?.[index]
			if (!question) {
				return null
			}
			let n = this.answernumbers[index]
			if (n != null && n !== "" && question.possibilities?.[n] != null) {
				return Number(n)
			}
			const fromText = this.findPossibilityIndex(question, this.answers[index])
			return fromText
		},
		rebuildAnswerNumbersFromStoredAnswers() {
			if (!Array.isArray(this.questions) || !this.questions.length) {
				return
			}
			const rebuilt = this.questions.map((question, index) => {
				const existing = this.answernumbers[index]
				if (existing != null && existing !== "" && question.possibilities?.[existing] != null) {
					return existing
				}
				return this.findPossibilityIndex(question, this.answers[index])
			})
			this.answernumbers = rebuilt
			this.receiveanswernumbers = [...rebuilt]
		},
		displayAnswer(index) {
			const q = this.questions[index]
			const selectedIndex = this.resolveAnswerIndex(index)
			if (q?.possibilities && selectedIndex != null && q.possibilities[selectedIndex] != null) {
				return q.possibilities[selectedIndex]
			}
			const stored = this.answers[index]
			if (stored != null && stored !== "") {
				return stored
			}
			return null
		},
		displayRecommendation(index) {
			const q = this.questions[index]
			const selectedIndex = this.resolveAnswerIndex(index)
			if (q?.recommendations && selectedIndex != null && q.recommendations[selectedIndex]) {
				return q.recommendations[selectedIndex]
			}
			return null
		},
		goToFillOut() {
			this.$router.push({
				path: "/nis",
				query: { nisID: this.nisID, Resubmit: 1 },
			}).then(() => {
				this.viewMode = false
				this.loadForm()
			})
		},
		async loadSubmittedAssessment(nisID) {
			const data = await NisApi.getNisAssessment(nisID)
			const assessment = Array.isArray(data) ? data[0] : data
			if (!assessment) {
				throw new Error("Could not load NIS assessment")
			}
			this.nisID = assessment.id || nisID
			this.resubmit = assessment.Resubmit === 1 || assessment.resubmit === 1 ? 1 : 0
			this.score1 = assessment.score1 == null ? null : Math.round(assessment.score1)
			this.score2 = assessment.score2 == null ? null : Math.round(assessment.score2)
			this.score3 = assessment.score3 == null ? null : Math.round(assessment.score3)
			this.score4 = assessment.score4 == null ? null : Math.round(assessment.score4)
			this.score5 = assessment.score5 == null ? null : Math.round(assessment.score5)
			this.score6 = assessment.score6 == null ? null : Math.round(assessment.score6)

			if (assessment.timestamp) {
				this.timestamp = assessment.timestamp
				const date = new Date(assessment.timestamp)
				const month = date.toLocaleString("default", { month: "short" })
				this.year = date.getFullYear()
				this.filledOutDate = `${month} ${this.year}`
			} else {
				this.year = new Date().getFullYear()
				this.filledOutDate = ""
			}

			this.name = `Orixe NIS Cyber Security ${this.year}`
			this.id = assessment.nis?.id || "2026"
			this.questions = assessment.nis?.questions || []
			this.fileID = assessment.nis?.fileID || null

			this.answers = this.parseJsonArray(assessment.answers)
			while (this.answers.length < this.questions.length) {
				this.answers.push(null)
			}

			this.answernumbers = this.parseJsonArray(assessment.AnswerNumbers)
			while (this.answernumbers.length < this.questions.length) {
				this.answernumbers.push(null)
			}
			this.rebuildAnswerNumbersFromStoredAnswers()
			this.viewMode = true
			this.$nextTick(() => {
				window.scrollTo({ top: 0, left: 0, behavior: "auto" })
			})
		},
		loadForm() {
			const isResubmit = String(this.$route.query.Resubmit || "") === "1"
			const routeId = this.assessmentId || this.$route.params.id
			this.loading = true
			this.loadError = null

			if (routeId && !isResubmit) {
				return this.loadSubmittedAssessment(routeId)
					.then(() => {
						this.loading = false
					})
					.catch(error => {
						this.loading = false
						this.loadError =
							(error && error.message) ||
							"Could not load NIS assessment."
						this.$store.dispatch(
							"loading/" + LoadingState.ACTION_SET_ERROR,
							error,
							{ root: true }
						)
					})
			}

			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				"Loading NIS survey...",
				{ root: true }
			)

			NisApi.getNisForm(isResubmit)
				.then(async data => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
					if (data.nisID && !isResubmit) {
						await this.loadSubmittedAssessment(data.nisID)
						this.loading = false
						return
					}
					this.viewMode = false
					const fillYear = new Date().getFullYear()
					this.year = fillYear
					this.name = `Orixe NIS Cyber Security ${fillYear}`
					this.id = data.id
					const questions = (data.questions || []).map((question, index) => {
						if (index === 0 && question.type === "title") {
							return {
								...question,
								name: `This is the Orixe NIS Cyber Security Self-Assessment for ${fillYear}. Click "Show Question Details" to find more info.`
							}
						}
						return question
					})
					if (!questions.length) {
						throw new Error("NIS form has no questions.")
					}
					this.$set(this, "questions", questions)
					this.fileID = data.fileID
					this.savedAnswers = []
					this.answers = []
					for (let i = 0; i < this.questions.length; i++) {
						this.answers.push(null)
						this.savedAnswers.push(null)
					}
					this.receiveanswernumbers = data.answernumbers || []
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					this.loadError =
						(error && error.message) ||
						"Could not load the NIS survey. Please try again."
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		buildAnswerNumbersPayload(answers) {
			if (!Array.isArray(this.questions) || !this.questions.length) {
				return [...this.answernumbers]
			}
			return this.questions.map((question, index) => {
				const stored = this.answernumbers[index]
				if (stored != null && stored !== "" && question.possibilities?.[stored] != null) {
					return stored
				}
				return this.findPossibilityIndex(question, answers[index])
			})
		},
		sendNisReport(answers) {
			const answerNumbers = this.buildAnswerNumbersPayload(answers)
			this.answernumbers = answerNumbers
			NisApi.submitForm(this.id, answers, JSON.stringify(answerNumbers))
				.then(async data => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully completed NIS Form with ID '${data}'`,
						{ root: true }
					)
					if (String(this.$route.path) !== "/nis") {
						await this.$router.replace("/nis")
					}
					await this.loadSubmittedAssessment(data)
				})
				.catch(error => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		saveNisReport(answers) {
			const answerNumbers = this.buildAnswerNumbersPayload(answers)
			this.answernumbers = answerNumbers
			NisApi.saveSubmission(this.id, answers, JSON.stringify(answerNumbers))
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully saved NIS Submission draft!`,
						{ root: true }
					)
					this.savedAnswers = JSON.parse(JSON.stringify(answers))
				})
				.catch(error => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		downloadCanvas() {
			const canvas = this.$refs.canvas
			if (!canvas) return
			const size = 600
			canvas.width = size
			canvas.height = size
			const context = canvas.getContext("2d")
			const clientName = this.$store.getters["user/getUser"].clientName || ""
			const imageObj = new Image()
			imageObj.onload = () => {
				context.clearRect(0, 0, size, size)
				context.fillStyle = "#ffffff"
				context.fillRect(0, 0, size, size)
				context.drawImage(imageObj, 0, 0, size, size)

				if (clientName) {
					const maxWidth = 260
					const centerX = 410
					const startY = 455
					context.font = "bold 18px Sarabun, Arial, sans-serif"
					context.fillStyle = "#0f4c52"
					context.textAlign = "center"
					context.textBaseline = "top"
					this.drawWrappedText(context, clientName, centerX, startY, maxWidth, 22)
				}

				const link = document.createElement("a")
				link.download =
					"orixe-nis-" +
					clientName.replace(/[^a-z0-9]/gi, "_").toLowerCase() +
					".png"
				link.href = canvas.toDataURL("image/png")
				link.click()
			}
			imageObj.onerror = () => {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					new Error("Could not load NIS badge image for download."),
					{ root: true }
				)
			}
			imageObj.src = require("../assets/Badge_NIS_2026.jpg")
		},
		drawWrappedText(context, text, x, y, maxWidth, lineHeight) {
			const words = String(text).trim().split(/\s+/)
			let line = ""
			let drawY = y
			for (let i = 0; i < words.length; i++) {
				const testLine = line ? `${line} ${words[i]}` : words[i]
				if (context.measureText(testLine).width > maxWidth && line) {
					context.fillText(line, x, drawY)
					line = words[i]
					drawY += lineHeight
				} else {
					line = testLine
				}
			}
			if (line) {
				context.fillText(line, x, drawY)
			}
		},
	}
}
</script>

<style scoped>
.nis-score-header {
	position: relative;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding-top: 8px;
	min-height: 56px;
}
.nis-score-heading {
	margin: 0;
	padding-right: 160px;
	padding-left: 160px;
	width: 100%;
	text-align: center;
}
.nis-fill-out-btn {
	position: absolute;
	top: 0;
	right: 12px;
	color: white !important;
	background-color: orange !important;
	font-weight: 600;
	letter-spacing: 0.02em;
}
.nis-score-copy {
	margin-left: 30px;
	margin-right: 20px;
	width: 100%;
}
.nis-dashboard-box {
	margin: 6px 0;
}
.nis-box-inner {
	height: 100%;
	min-height: 220px;
	margin: 8px;
	padding: 14px;
	border: 1px solid #ddd;
	border-radius: 10px;
	background: #fff;
	display: flex;
	flex-direction: column;
}
.nis-badge-inner {
	align-items: center;
	justify-content: center;
	text-align: center;
}
.nis-badge-title {
	font-size: 1.05rem;
	font-weight: 700;
	line-height: 1.25;
	margin-bottom: 10px;
	color: #3d2645;
}
.nis-badge-preview {
	display: block;
	width: 100%;
	max-width: 160px;
	height: auto;
	margin: 0 auto;
}
.nis-download-btn {
	min-width: 130px;
	max-width: 180px;
	height: 32px !important;
	font-size: 0.85rem !important;
	color: white !important;
	text-transform: none;
	letter-spacing: 0.02em;
}
.nis-filled-out {
	font-size: 0.85rem;
	margin-top: auto;
	padding-top: 8px;
}
.nis-badge-canvas {
	position: absolute;
	left: -9999px;
	top: 0;
	width: 600px;
	height: 600px;
	opacity: 0;
	pointer-events: none;
}
@media (max-width: 960px) {
	.nis-box-inner {
		margin: 6px 0;
	}
}
</style>
